import { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { generateToken, AGORA_APP_ID } from '../../services/agoraToken';
import { doc, updateDoc, onSnapshot, increment, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function AgentCallScreen({ channelName, sessionId, agentId, onEnd }) {
  const [seconds, setSeconds] = useState(0);
  const secondsRef = useRef(0);
  const [muted, setMuted] = useState(false);
  const clientRef = useRef(null);
  const localTrackRef = useRef(null);
  const timerRef = useRef(null);
  const hasEndedRef = useRef(false);

  useEffect(() => {
    startCall();

    const unsubscribe = onSnapshot(doc(db, 'sessions', sessionId), (snap) => {
      if (snap.exists()) {
        const status = snap.data().status;

        if (status === 'completed' && !hasEndedRef.current) {
          hasEndedRef.current = true;
          cleanup();
          const sessionData = snap.data();
          const agentEarnings = sessionData.agentEarnings || 0;
          console.log('Customer ended — agentEarnings:', agentEarnings, 'agentId:', agentId);
          if (agentEarnings > 0 && agentId) {
            updateDoc(doc(db, 'agents', agentId), {
              totalEarnings: increment(agentEarnings),
              pendingWithdrawal: increment(agentEarnings),
              totalSessions: increment(1),
            }).then(() => console.log('Agent earnings updated!'))
              .catch(e => console.error('Earnings error:', e.message));
          }
          onEnd();
        }

        if (status === 'emergency' && !hasEndedRef.current) {
          hasEndedRef.current = true;
          cleanup();
          onEnd();
        }
      }
    });

    return () => { cleanup(); unsubscribe(); };
  }, []);

  const startCall = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const token = await generateToken(channelName);

      clientRef.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      const client = clientRef.current;

      await client.join(AGORA_APP_ID, channelName, token, null);

      localTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack();
      await client.publish(localTrackRef.current);

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "audio") {
          user.audioTrack.play();
        }
      });

      await updateDoc(doc(db, 'sessions', sessionId), {
        status: 'ongoing'
      });

    } catch (error) {
      console.error("Agent Agora error:", error.message);
    }

    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
    }, 1000);
  };

  const cleanup = async () => {
    clearInterval(timerRef.current);
    if (localTrackRef.current) {
      localTrackRef.current.close();
    }
    if (clientRef.current) {
      await clientRef.current.leave();
    }
  };

  const handleEnd = async () => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;
    clearInterval(timerRef.current);
    await cleanup();

    const sessionSnap = await getDoc(doc(db, 'sessions', sessionId));

    if (sessionSnap.exists()) {
      const ratePerSecond = sessionSnap.data().ratePerSecond || 2;
      const coinsUsed = secondsRef.current * ratePerSecond;
      const agentEarnings = Math.floor(coinsUsed * 0.6);

      await updateDoc(doc(db, 'sessions', sessionId), {
        status: 'completed',
        duration: secondsRef.current,
        coinsUsed,
        agentEarnings,
      });

      if (agentEarnings > 0) {
        await updateDoc(doc(db, 'agents', agentId), {
          totalEarnings: increment(agentEarnings),
          pendingWithdrawal: increment(agentEarnings),
          totalSessions: increment(1),
        });
        console.log('Agent earnings updated from agent end:', agentEarnings);
      }
    }

    onEnd();
  };

  const handleEmergency = async () => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;
    clearInterval(timerRef.current);
    await cleanup();
    await updateDoc(doc(db, 'sessions', sessionId), {
      status: 'emergency',
    });
    alert('🛑 Emergency stop. Report filed.');
    onEnd();
  };

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={styles.container}>
      <div style={styles.liveBadge}>● LIVE SESSION</div>
      <div style={styles.avatar}>🎧</div>
      <div style={styles.title}>Session in Progress</div>
      <div style={styles.sub}>Anonymous Customer</div>

      <div style={styles.timerBox}>
        <div style={styles.timer}>{fmt(seconds)}</div>
        <div style={styles.timerSub}>Session duration</div>
      </div>

      <div style={styles.controls}>
        <button
          style={{
            ...styles.muteBtn,
            backgroundColor: muted ? '#ef4444' : '#ffffff15',
          }}
          onClick={async () => {
            if (localTrackRef.current) {
              await localTrackRef.current.setMuted(!muted);
            }
            setMuted(!muted);
          }}
        >
          {muted ? '🔇' : '🔊'}
        </button>
        <button style={styles.endBtn} onClick={handleEnd}>
          End Session
        </button>
      </div>

      <button style={styles.emergencyBtn} onClick={handleEmergency}>
        🛑 Emergency Stop
      </button>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0f0f1a',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 24px 24px',
    fontFamily: 'sans-serif',
  },
  liveBadge: {
    color: '#22c55e',
    fontWeight: '700',
    fontSize: '12px',
    letterSpacing: '2px',
    marginBottom: '24px',
  },
  avatar: {
    width: '100px',
    height: '100px',
    borderRadius: '28px',
    backgroundColor: '#2d1b69',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    border: '3px solid #f97316',
    marginBottom: '16px',
  },
  title: {
    color: '#ffffff',
    fontWeight: '900',
    fontSize: '22px',
    marginBottom: '4px',
  },
  sub: {
    color: '#ffffff60',
    fontSize: '14px',
    marginBottom: '32px',
  },
  timerBox: {
    backgroundColor: '#ffffff08',
    borderRadius: '16px',
    padding: '16px 32px',
    textAlign: 'center',
    marginBottom: '40px',
  },
  timer: {
    color: '#ffffff',
    fontSize: '48px',
    fontWeight: '900',
    fontVariantNumeric: 'tabular-nums',
  },
  timerSub: {
    color: '#ffffff50',
    fontSize: '12px',
    marginTop: '4px',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    width: '100%',
    maxWidth: '400px',
    marginBottom: '12px',
  },
  muteBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '18px',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  endBtn: {
    flex: 1,
    backgroundColor: '#ef4444',
    border: 'none',
    borderRadius: '18px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'pointer',
  },
  emergencyBtn: {
    backgroundColor: '#ef444415',
    border: '1px solid #ef444450',
    borderRadius: '14px',
    padding: '14px 28px',
    color: '#ef4444',
    fontWeight: '800',
    fontSize: '14px',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '400px',
  },
};